import React, { useEffect, useState } from "react";
import { Mic, Send, Star, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * PostClaimHollerNudge — appears on the Claim success moment, right after a
 * hunter submits their selfie/proof. Pulls on the emotional peak to capture
 * a testimonial while they're still pumped.
 *
 * Design rules:
 * - Only renders if the Holler is globally enabled.
 * - One dismissible entry point ("Got a sec? Holler at us 🎤") + an inline
 *   mini-form (stars + story) so the hunter never leaves the claim page.
 * - After submission, turns into a confirmation card with a CTA to visit
 *   the full Holler page. No fifth dismiss button — keep it tight.
 *
 * Props:
 *   name       — prefill (username/display name if known)
 *   city       — prefill (from the drop/quest)
 *   didWin     — boolean, defaults to true on this surface
 *   context    — free-form string to slip into placeholder ("the hunt" / "this quest")
 *   onDismiss? — optional callback when user closes
 */
export default function PostClaimHollerNudge({
  name = "",
  city = "",
  didWin = true,
  context = "the hunt",
  onDismiss,
}) {
  const [enabled, setEnabled] = useState(null); // null = checking
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [closed, setClosed] = useState(false);
  const [form, setForm] = useState({
    name: name || "",
    city: city || "",
    rating: 5,
    body: "",
    did_win: !!didWin,
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/holler", { params: { limit: 1 } }).then(({ data }) => {
      if (!cancelled) setEnabled(data?.enabled !== false);
    }).catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  if (enabled === null || enabled === false || closed) return null;

  const close = () => { setClosed(true); onDismiss?.(); };

  const submit = async () => {
    if (form.body.trim().length < 10) {
      toast.error("Tell us at least a sentence (10+ chars).");
      return;
    }
    if (!form.name.trim()) {
      toast.error("A name or handle, please.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/holler", {
        name: form.name.trim(),
        city: form.city.trim() || null,
        body: form.body.trim(),
        rating: form.rating || null,
        did_win: !!form.did_win,
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="loot-card-flat p-5 border-2 border-[#76B82A]"
        data-testid="holler-nudge-done"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 size={24} className="text-[#76B82A] shrink-0" />
          <div className="flex-1">
            <div className="font-display uppercase text-xl">Thanks for hollering.</div>
            <p className="text-sm text-[#F5F2E8]/75 mt-1">
              We'll review and post it on the wall soon. Now go celebrate — you earned it.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <a href="/holler" className="btn-ghost text-xs">See the Holler</a>
              <button onClick={close} className="btn-ghost text-xs">Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="loot-card-flat p-5 relative border-2 border-[#D7A62A]"
      data-testid="holler-nudge"
    >
      <button
        onClick={close}
        className="absolute top-2 right-2 p-1 text-[#F5F2E8]/50 hover:text-[#E53935]"
        aria-label="Close"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 border-2 border-[#D7A62A] flex items-center justify-center">
          <Mic size={18} className="text-[#D7A62A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline" style={{ color: "#D7A62A" }}>One more thing</div>
          <h3 className="font-display uppercase text-xl leading-tight mt-0.5">
            Got 30 seconds? Holler at us.
          </h3>
          <p className="text-sm text-[#F5F2E8]/75 mt-1">
            Tell other hunters what {context} was like. Win, skunked, or somewhere in between — your
            voice is what keeps this whole thing moving.
          </p>

          {!expanded ? (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setExpanded(true)}
                className="btn-loot text-xs inline-flex items-center gap-1"
                data-testid="holler-nudge-start"
              >
                <Mic size={12} /> Drop a holler
              </button>
              <button
                onClick={close}
                className="btn-ghost text-xs"
                data-testid="holler-nudge-skip"
              >
                Skip
              </button>
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  className="loot-input"
                  placeholder="Name or @handle"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={80}
                  data-testid="holler-nudge-name"
                />
                <input
                  className="loot-input"
                  placeholder="City (optional)"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  maxLength={80}
                />
              </div>
              <textarea
                className="loot-input"
                rows={3}
                placeholder={`How was ${context}? What moment will stick with you?`}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                maxLength={1500}
                data-testid="holler-nudge-body"
              />
              <div className="flex items-center gap-1">
                <span className="font-mono text-xs text-[#F5F2E8]/55 mr-1">Stars:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-0.5"
                    aria-label={`${n} stars`}
                  >
                    <Star
                      size={18}
                      fill={n <= form.rating ? "#D7A62A" : "transparent"}
                      stroke={n <= form.rating ? "#D7A62A" : "rgba(245,242,232,0.45)"}
                    />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <button
                  onClick={submit}
                  disabled={busy}
                  className="btn-loot text-xs inline-flex items-center gap-1"
                  data-testid="holler-nudge-submit"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  {busy ? "Sending…" : "Send my holler"}
                </button>
                <button onClick={close} className="btn-ghost text-xs">Skip</button>
                <span className="ml-auto font-mono text-[10px] text-[#F5F2E8]/45">
                  Reviewed by hand before posting.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
